/**
 * @fileoverview SortCategoriesModal - Modal for reordering categories.
 *
 * Allows users to customize the display order of categories through:
 * - Drag and drop reordering
 * - Manual sort number editing
 * - Auto-renumbering (1, 2, 3... with no gaps)
 *
 * @module components/CategoryManagement/SortCategoriesModal
 */
import { useState, useEffect } from 'react';
import ButtonSpinner from '../Loading/ButtonSpinner';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './SortCategoriesModal.module.css';

/**
 * SortCategoriesModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Array} props.categories - Array of category objects
 * @param {Function} props.onSave - Callback when save is clicked
 */
function SortCategoriesModal({ isOpen, onClose, categories, onSave }) {
    const [sortedCategories, setSortedCategories] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useModalScrollLock(isOpen);

    // Reset saving state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsSaving(false);
        }
    }, [isOpen]);

    // Initialize sorted categories when modal opens
    useEffect(() => {
        if (isOpen && categories) {
            // Sort by current sortOrder, fallback to name
            const sorted = [...categories].sort((a, b) => {
                if (a.sortOrder === b.sortOrder) {
                    return a.categoryName.localeCompare(b.categoryName);
                }
                return (a.sortOrder || 0) - (b.sortOrder || 0);
            });

            // Assign sequential sort orders (1, 2, 3...)
            const withSortOrder = sorted.map((cat, index) => ({
                ...cat,
                sortOrder: index + 1
            }));

            setSortedCategories(withSortOrder);
        }
    }, [isOpen, categories]);

    if (!isOpen) return null;

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newCategories = [...sortedCategories];
        const draggedItem = newCategories[draggedIndex];

        // Remove from old position
        newCategories.splice(draggedIndex, 1);
        // Insert at new position
        newCategories.splice(index, 0, draggedItem);

        // Renumber sequentially
        const renumbered = newCategories.map((cat, idx) => ({
            ...cat,
            sortOrder: idx + 1
        }));

        setSortedCategories(renumbered);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleSortOrderChange = (index, newValue) => {
        const num = parseInt(newValue);
        if (isNaN(num) || num < 1) return;

        const newCategories = [...sortedCategories];
        newCategories[index] = {
            ...newCategories[index],
            sortOrder: num
        };

        // Sort by the new sort order values
        newCategories.sort((a, b) => a.sortOrder - b.sortOrder);

        // Renumber sequentially to remove gaps
        const renumbered = newCategories.map((cat, idx) => ({
            ...cat,
            sortOrder: idx + 1
        }));

        setSortedCategories(renumbered);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Send array of { categoryKey, sortOrder }
            const updates = sortedCategories.map(cat => ({
                categoryKey: cat.categoryKey,
                sortOrder: cat.sortOrder
            }));

            await onSave(updates);
            onClose();
            // Note: Don't set isSaving to false - modal will close
        } catch (err) {
            setError(err.message || 'Failed to update sort order');
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    return (
        <>
            {/* Dark Overlay */}
            <div className={styles.overlay} onClick={handleClose}>
                {/* Modal Content */}
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    {/* Close X Button */}
                    <button
                        onClick={handleClose}
                        disabled={isSaving}
                        className={styles.closeButton}
                        aria-label="Close"
                    >
                        ×
                    </button>

                    {/* Modal Header */}
                    <div className={styles.header}>
                        <h2 className={styles.title}>Edit Category Sort Order</h2>
                        <p className={styles.subtitle}>
                            Drag rows to reorder, or edit the order number directly.
                            Changes are auto-sorted and renumbered.
                        </p>
                    </div>

                    {/* Modal Body */}
                    <div className={styles.body}>
                        {error && (
                            <div className={styles.error}>
                                {error}
                            </div>
                        )}

                        <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th className={styles.dragHeader}>Drag</th>
                                        <th className={styles.orderHeader}>Order</th>
                                        <th className={styles.nameHeader}>Category Name</th>
                                        <th className={styles.statusHeader}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedCategories.map((category, index) => (
                                        <tr
                                            key={category.categoryKey}
                                            className={draggedIndex === index ? styles.dragging : ''}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <td className={styles.dragCell}>
                                                <span className={styles.dragHandle}>⋮⋮</span>
                                            </td>
                                            <td className={styles.orderCell}>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={category.sortOrder}
                                                    onChange={(e) => handleSortOrderChange(index, e.target.value)}
                                                    className={styles.orderInput}
                                                />
                                            </td>
                                            <td className={styles.nameCell}>
                                                {category.categoryName}
                                                {category.userKey && (
                                                    <span className={styles.userBadge}> (Personal)</span>
                                                )}
                                            </td>
                                            <td className={styles.statusCell}>
                                                {category.endDate ? (
                                                    <span className={styles.ceasedBadge}>Ceased</span>
                                                ) : (
                                                    <span className={styles.activeBadge}>Active</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Modal Footer */}
                    <div className={styles.footer}>
                        <div className={styles.buttonGroup}>
                            <button
                                onClick={handleClose}
                                disabled={isSaving}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className={styles.saveButton}
                            >
                                {isSaving && <ButtonSpinner />}
                                {isSaving ? 'Saving Order...' : 'Save Order'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default SortCategoriesModal;
